import { Component, OnInit, ViewChild } from "@angular/core";
import moment from "moment";
import { NgxSpinnerService } from "ngx-spinner";
import { AppService } from "src/app/core/services/app.service";
import { FocuslinkService } from "src/app/core/services/focuslink.service";
import { ImageCropperService } from "src/app/core/services/image-cropper.service";
import { UserService } from "src/app/core/services/user.service";
import { OOVenueService } from "src/app/core/services/venue-service";
import { CheckDto, CheckItemDto, CheckSeatDto } from "src/app/shared/models/check-dto";
import { EGiftTransaction } from "src/app/shared/models/eGift/EGiftTransaction.model";
import { EGiftVenue } from "src/app/shared/models/eGift/EGiftVenue.model";
import * as uuid from "uuid";
import { EGiftTransactionRecordComponent } from "./e-gift-transaction-record/e-gift-transaction-record.component";

@Component({
	selector: "app-e-gift-transactions",
	templateUrl: "./e-gift-transactions.component.html",
	styleUrls: ["../../commandcenter.scss"],
})
export class EGiftTransactionsComponent implements OnInit {
	@ViewChild("transactionRecord") transactionRecord: EGiftTransactionRecordComponent;
	adHocTransaction: boolean = false;

	eGiftVenue: EGiftVenue;

	moment: any = moment;

	searchDateRange: Date[];

	transactions: EGiftTransaction[];
	transactionsFiltered: EGiftTransaction[];
	transactionSelected: EGiftTransaction;

	constructor(
		public app: AppService,
		private focuslink: FocuslinkService,
		private spinner: NgxSpinnerService,
		public userService: UserService,
		public imgCrop: ImageCropperService,
		public venueService: OOVenueService
	) {}

	ngOnInit(): void {
		this.getEgiftVenue(this.venueService.subscriptionVenue.value.key);
		this.getTransactions(this.venueService.subscriptionVenue.value.key);

		this.venueService.subscriptionVenue.subscribe(async (venue) => {
			this.getEgiftVenue(venue.key);
			this.getTransactions(venue.key);
		});
	}

	async adhocTransaction() {
		this.adHocTransaction = true;
		this.transactionSelected = new EGiftTransaction({
			venueKey: this.venueService.subscriptionVenue.value.key,
			key: uuid.v4(),
		});
	}

	async adhocTransactionSubmit(rec: EGiftTransaction) {
		this.app.confirmation.confirm({
			message: `Are you sure you want to send a $${rec.value} EGift Card to ${rec.recipientName} at ${rec.recipientEmail}?`,
			accept: async () => {
				this.app.loading(true);
				this.adHocTransaction = false;
				this.transactionSelected = rec;
				const ooMenuConfig = await this.focuslink.getOOMenuConfig(this.venueService.subscriptionVenue.value.key);
				this.transactionSelected.check = new CheckDto({
					venueKey: this.eGiftVenue.venueKey,
					source: "focuspos-egift",
					checkKey: uuid.v4(),
					table: this.transactionSelected.purchaserLastName,
					numberOfGuests: 1,
					employeeId: ooMenuConfig.employeeRecNum,
					seats: [
						new CheckSeatDto({
							seatKey: 1,
							items: [
								new CheckItemDto({
									menuItemKey: this.eGiftVenue.menuItemKey,
									name: `Gift Card Sale`,
									quantity: 1,
									price: this.transactionSelected.value,
									modifiers: [],
								}),
							],
						}),
					],
				});

				const resp = await this.focuslink.eGiftTransactionPost(this.eGiftVenue.venueKey, this.transactionSelected);
				this.transactionSelected = undefined;
				this.adHocTransaction = false;
				this.app.loading(false);
			},
			reject: async () => {
				this.transactionSelected = undefined;
				this.adHocTransaction = false;
				this.app.loading(false);
				this.app.confirmation.close();
			},
		});
	}

	async balance(cardNumber) {
		try {
			const resp = await this.focuslink.getGiftBalance(this.venueService.subscriptionVenue.value.key, cardNumber);
			return resp.balance;
		} catch (error) {
			console.log(error);
			return 0;
		}
	}

	async getEgiftVenue(venueKey) {
		const resp = await this.focuslink.eGiftVenueGet(venueKey);
		this.eGiftVenue = new EGiftVenue(resp);
	}

	async getTransactions(venueKey) {
		const resp = await this.focuslink.eGiftTransactionGetAll(venueKey);
		const trxns: EGiftTransaction[] = resp.map((i) => {
			const t = new EGiftTransaction(i);
			t.transactionDateTime = moment(t.transactionDateTime).toDate();
			return t;
		});
		this.transactions = trxns.sort((a, b) => (moment(a.transactionDateTime).unix() < moment(b.transactionDateTime).unix() ? 1 : -1));
		this.transactionsFiltered = this.transactions;
	}

	async resend(transaction: EGiftTransaction) {
		this.app.confirmation.confirm({
			message: `Are you sure you want to resend the EGift Email to ${transaction.recipientEmail}`,
			accept: async () => {
				await this.focuslink.eGiftTransactionResend(
					this.venueService.subscriptionVenue.value.key,
					transaction.key,
					this.userService.userSubscription.value.email
				);
				this.app.msgSvc.add({ severity: "success", detail: "Gift card resent" });
			},
			reject: async () => {
				this.app.confirmation.close();
			},
		});
	}

	returnFromEditTransaction(editedTransaction: EGiftTransaction) {
		const idx = this.transactions.findIndex((i) => i.key === editedTransaction.key);
		this.transactions[idx] = editedTransaction;
	}

	async transactionInfo(rec: EGiftTransaction) {
		this.app.loading(true);
		const resp = await this.focuslink.eGiftTransactionGetOne(this.venueService.subscriptionVenue.value.key, rec.key);
		this.transactionSelected = new EGiftTransaction(resp);
		if (this.transactionSelected.activated && this.transactionSelected.EGiftCardPool?.cardNumber) {
			this.transactionSelected.EGiftCardPool.cardBalance = await this.balance(this.transactionSelected.EGiftCardPool.cardNumber);
		}
		this.app.loading(false);
	}

	async filterDateRange(event) {
		if (this.searchDateRange.length === 2 && this.searchDateRange[1] !== null) {
			this.transactionsFiltered = this.transactionsFiltered.filter((i) =>
				moment(i.transactionDateTime).isBetween(moment(this.searchDateRange[0]), moment(this.searchDateRange[1]))
			);
		}
	}

	async filterClearRange() {
		this.transactionsFiltered = this.transactions;
	}
}
